import { useMemo } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useParams } from "react-router-dom";
import { DIRECT_CHAT_ROOM } from "@/react/components/constants";
import { useChatsSidebarApi } from "@/react/hooks/chatsV2/sidebar/useChatsSidebarApi";
import { useWebSocket } from "@/react/hooks/useWebSocket";
import { BotChatItem } from "@circle-react/components/Modals/MessagingModal/LeftSidebar/List/BotChatItem";
import { usePinnedBotChat } from "@circle-react/components/SettingsApp/CommunityInbox/hooks/usePinnedBotChat";
import { usePunditUserContext } from "@circle-react/contexts";
import { dateStringToTimeAgo } from "@circle-react/helpers/dateTimeHelpers/timeAgo";
import { Loader } from "@circle-react-uikit/Loader";
import { usePopoverPortalContext } from "@circle-react-uikit/PopoverPortal";
import { Item } from "./Item";
import { ItemWrapper } from "./Item/ItemWrapper";
import { NoMessages } from "./Item/NoMessages";
import { ListWrapper } from "./ListWrapper";

const MAX_UNREAD_UUIDS = 2000;

export interface ListProps {
  unreadChatRoomUuids?: string[];
}

interface Params {
  uuid: string;
}

export const List = ({ unreadChatRoomUuids }: ListProps) => {
  const { uuid: chatRoomUuid } = useParams<Params>();
  const { onClose } = usePopoverPortalContext() || {};
  const { currentCommunityMember, isLoading: isCommunityLoading } =
    usePunditUserContext();

  const {
    chatRooms,
    hasNextPage,
    isLoading: isChatRoomsLoading,
    onEventReceive,
    fetchNextPage,
  } = useChatsSidebarApi({
    openChatRoomUuid:
      chatRoomUuid && chatRoomUuid !== "new" ? chatRoomUuid : null,
    uuids: unreadChatRoomUuids?.slice(0, MAX_UNREAD_UUIDS),
  });

  useWebSocket(
    {
      channel: "ChatRoomChannel",
      onMessageReceive: data => {
        onEventReceive(data, currentCommunityMember?.id);
      },
      community_member_id: currentCommunityMember?.id,
      canCreateConnection: true,
    },
    [chatRooms],
  );

  const { botChat, isLoading: isBotChatLoading } = usePinnedBotChat();
  const shouldShowBotChat = Boolean(botChat);

  const isLoading =
    isCommunityLoading || isChatRoomsLoading || isBotChatLoading;

  const chatRoomWithMessages = useMemo(
    () => chatRooms.filter(chatRoom => chatRoom.last_message),
    [chatRooms],
  );
  const isEmpty = !chatRoomWithMessages?.length && !botChat;

  if (isLoading || !currentCommunityMember) {
    return (
      <ListWrapper>
        <Loader center />
      </ListWrapper>
    );
  }

  if (isEmpty) {
    return (
      <ListWrapper>
        <NoMessages />
      </ListWrapper>
    );
  }
  return (
    <ListWrapper>
      {shouldShowBotChat && <BotChatItem chat={botChat} />}
      <InfiniteScroll
        style={{ height: "100%", overflow: "hidden", marginTop: "8px" }}
        scrollThreshold={0.8}
        next={fetchNextPage}
        hasMore={hasNextPage}
        dataLength={chatRoomWithMessages.length}
        scrollableTarget="scrollable-dms-div"
        loader={<Loader center />}
      >
        {chatRoomWithMessages.map(
          ({
            chat_room_name,
            chat_room_kind,
            last_message,
            current_participant = {},
            other_participants_preview: otherParticipants = [],
            id,
            has_unread_messages,
            uuid,
            pinned_at,
          }) => {
            const isDirect = chat_room_kind === DIRECT_CHAT_ROOM;
            return (
              <ItemWrapper key={id} onClose={onClose} uuid={uuid}>
                <Item
                  chatRoomName={chat_room_name}
                  lastMessage={last_message}
                  currentParticipant={current_participant}
                  otherParticipants={otherParticipants}
                  isDirect={isDirect}
                  hasUnreadMessages={has_unread_messages}
                  timestamp={dateStringToTimeAgo(last_message?.created_at, {
                    format: "short",
                  })}
                  currentCommunityMember={currentCommunityMember}
                  isPinned={!!pinned_at}
                />
              </ItemWrapper>
            );
          },
        )}
      </InfiniteScroll>
    </ListWrapper>
  );
};
